import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Modal } from '@/components/CustomModal'
import { ClientDto } from 'api/Clients/Types/Client'
import { PaymentMethodReplyDto } from 'api/PaymentProvider/Types/PaymentMethodReplyDto'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { WithdrawalFromWalletPspTransaction } from 'api/Transaction/Types/WithdrawalFromWalletPspTransaction'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { AxiosError, isAxiosError } from 'axios'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTransactionStatusAction } from 'store/transactions/transaction.action'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty } from 'utils/is-empty'

import { WithdrawalForm, WithdrawalFormValues } from './WithdrawalForm'

import styles from '../../../Tickets/Tickets/Tickets.module.scss'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto

  setClient(): void
}

const WithdrawalPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [paymentProviderFields, setPaymentProviderFields] = useState<PaymentProviderFields[]>([])
  const [paymentProviders, setPaymentProviders] = useState<PaymentMethodReplyDto[]>([])
  const [wallet, setWallet] = useState<WalletDto>()
  const [paymentProviderId, setPaymentProviderId] = useState<string>('')

  const [form, setForm] = useState<WithdrawalFormValues>()
  const [confirmationData, setConfirmationData] = useState<WithdrawalFromWalletPspTransaction>()
  const [isConfirmationModalOpen, setConfirmationModal] = useState<boolean>(false)
  const [isNegativeBalanceCheckModalOpen, setNegativeBalanceCheckModalOpen] =
    useState<boolean>(false)

  const transactionState = useAppSelector((state) => state.transactions.status)
  const setToast = useContext(ToastContext)
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client])

  useEffect(() => {
    // reset paymentProviderId on change client
    setPaymentProviderId('')
    setPaymentProviderFields([])
  }, [client, wallet])

  useEffect(() => {
    if (isEmpty(transactionState)) dispatch(fetchTransactionStatusAction(walletsApiClient))
  }, [transactionState, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: wallets, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (preSelectedWallet) {
      setWallet(preSelectedWallet)
      getPaymentProvider(preSelectedWallet)
    }
  }, [preSelectedWallet, client]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: WithdrawalFromWalletPspTransaction) => {
    setConfirmationData(values)
    setConfirmationModal(true)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const response =
          await walletsApiClient.createWithdrawalFromWalletByPspTransaction(confirmationData)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: `Withdrawal has been submitted.`,
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: getIdFromLocationHeader(response.headers.location),
          }),
        })
        forceRefresh()
        setConfirmationModal(false)
      } catch (error) {
        const err = error as AxiosError
        if (isAxiosError(err.response)) {
          if (
            err.response.response?.data.code === 'not_enough_trading_account_equity' ||
            err.response.response?.data.code === 'not_enough_trading_account_equities'
          ) {
            setNegativeBalanceCheckModalOpen(true)
          }
        }
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModal(false)
  }

  const getPaymentProvider = async (walletDto: WalletDto) => {
    if (client) {
      setLoading(true)
      const response = await walletsApiClient.getAvailablePaymentProviders('withdrawals', {
        walletId: walletDto.id,
        languageId: 'en',
        caller: 'bo',
      })
      setPaymentProviders(response.reply)
      setLoading(false)
    }
  }

  const handleChangeWallet = async (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    if (walletDto && walletId) {
      setWallet(walletDto)
      setCurrency(walletDto.currency.id)
      await getPaymentProvider(walletDto)
    } else {
      setPaymentProviders([])
      setPaymentProviderFields([])
    }
  }

  const getPaymentProviderFields = async (pspId: string) => {
    setPaymentProviderId(pspId)
    if (client && pspId && wallet) {
      setLoading(true)
      const response = await walletsApiClient.getPaymentProviderField('withdrawals', {
        walletId: wallet.id,
        methodId: pspId,
        languageId: 'en',
        caller: 'bo',
      })
      setPaymentProviderFields(response.reply.form)
      // SC DRAGONPOHENIX IDR - in this case, we want to set wallet currency to IDR!
      const scDragonphoenix = paymentProviders.find((x) => x.name === 'SC Dragonphoenix IDR')
      if (scDragonphoenix) {
        setCurrency('IDR')
      }
      setLoading(false)
    } else {
      setPaymentProviderFields([])
    }
  }

  const selectFromWalletName = () => {
    return wallets?.find((x) => x.id === confirmationData?.fromWalletId)?.name
  }

  const selectToWalletName = () => {
    return paymentProviders?.find((x) => x.method_id === confirmationData?.toPspId)?.method_name
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to withdraw funds from the wallet {selectFromWalletName()}. The
                parameters selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>From Wallet:</strong> {selectFromWalletName()}
                </div>
                <div id='field'>
                  <strong>To Payment Provider:</strong> {selectToWalletName()}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {confirmationData?.amount} {wallet?.currency.id}
                </div>

                {paymentProviderFields?.length > 0 &&
                  paymentProviderFields.map((x) => {
                    if (x.systemFilled) {
                      return (
                        <div id='field'>
                          <strong>{x.name}:</strong> <>{x.value}</>
                        </div>
                      )
                    }
                    return (
                      <div id='field'>
                        <strong>{x.name}:</strong> {form?.options[x.field]}
                      </div>
                    )
                  })}
              </div>
            </div>
          }
        />
      )}
      {isNegativeBalanceCheckModalOpen && (
        <Modal
          visible={isNegativeBalanceCheckModalOpen}
          onClose={() => setNegativeBalanceCheckModalOpen(false)}
        >
          <div className={styles.borderBottom}>Negative Equity Check</div>
          <div className={styles.textContainer}>
            This client has negative equity in one of the trading accounts.
          </div>
        </Modal>
      )}
      <WithdrawalForm
        isLoading={loading}
        wallet={wallet}
        paymentProviderId={paymentProviderId}
        setWallet={handleChangeWallet}
        paymentProviderFields={paymentProviderFields}
        paymentProviders={paymentProviders}
        getPaymentProviderFields={getPaymentProviderFields}
        currency={currency}
        setCurrency={setCurrency}
        client={client}
        setClient={setClient}
        wallets={wallets || []}
        setForm={setForm}
        onSubmit={onSubmit}
        transactionState={transactionState}
      />
    </React.Fragment>
  )
}

export default WithdrawalPage
